import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  TableIcon,
  PresentationChartBarIcon,
  PresentationChartLineIcon,
} from '@heroicons/react/outline';
import Card from 'partial/components/Card';
import Table from 'partial/components/Table';
import Button from 'partial/components/Button';
import { BarChart, HorizontalBarChart } from 'modules/common/chart/BarChart';
import LineChart from 'modules/common/chart/LineChart';
import HistogramRange from 'partial/components/HistogramRange';
import PieChart from 'modules/common/chart/PieChart';

export const useHistogramCard = (array, defaultIndex = 0) => {
  const [props, setProps] = React.useState(array[defaultIndex]);
  const output = React.useMemo(
    () => ({
      key: props.id,
      ...props,
      actionOptions: array.map((x) => ({
        id: x.id,
        label: x.label,
        onClick: () => {
          setProps(x);
        },
      })),
    }),
    [array, props, setProps],
  );
  return output;
};

const MAP_TYPE = {
  day: 'hour',
  month: 'day',
  year: 'month',
};

function HistogramCard({
  hook,
  label,
  dateRange,
  switchMode,
  defaultSort,
  defaultMode,
  tableFormat,
  defaultChart,
  actionOptions,
  filter: otherFilter,
  renderSummary,
  customHeight,
  cardClassName,
  containerClass,
  extraParam,
}) {
  const [chartType, setChartType] = React.useState(defaultChart);
  const [meta, setMeta] = React.useState({ sort: defaultSort });
  const [filter, setFilter] = React.useState({
    type: MAP_TYPE[defaultMode],
    from: dayjs().startOf(defaultMode).format('YYYY-MM-DD'),
    to: dayjs().endOf(defaultMode).format('YYYY-MM-DD'),
  });
  const newFilter = React.useMemo(
    () => ({
      ...otherFilter,
      ...filter,
    }),
    [otherFilter, filter],
  );

  const [isLoading, table, chart, subLabel = '', summary = {}] = hook(
    newFilter,
    extraParam,
  );
  const toggleChart = React.useMemo(
    () => (
      <div className="grid grid-cols-1 sm:flex gap-1 items-center">
        {dateRange && (
          <div className="space-x-1 flex mt-2 sm:mt-0">
            <HistogramRange
              onChange={setFilter}
              defaultValue={dayjs().format('YYYY-MM-DD')}
              defaultMode={defaultMode}
              switchMode={switchMode}
            />
          </div>
        )}
        <div className="space-x-1 flex justify-end">
          <Button
            size="xs"
            primary={chartType === 'table'}
            icon={TableIcon}
            onClick={() => setChartType('table')}
          />
          <Button
            size="xs"
            primary={chartType === 'line'}
            icon={PresentationChartLineIcon}
            onClick={() => setChartType('line')}
          />
          <Button
            size="xs"
            primary={chartType === 'bar'}
            icon={PresentationChartBarIcon}
            onClick={() => setChartType('bar')}
          />
        </div>
      </div>
    ),
    [setFilter, chartType, setChartType, defaultMode, switchMode, dateRange],
  );

  const summaryFunc = () => {
    if (typeof renderSummary === 'function') return renderSummary(summary);
    return null;
  };

  return (
    <Card
      label={label}
      actionOptions={actionOptions}
      renderAction={renderSummary ? summaryFunc() : toggleChart}
      className={cardClassName}
    >
      <div
        className={cn('flex flex-col pr-3', containerClass)}
        style={{ height: customHeight ?? 300 }}
      >
        {chartType === 'table' && (
          <Table
            isLoading={isLoading}
            data={table}
            format={tableFormat}
            downloadCsv={(row) => ({
              Label: row.label,
              Txn: row.transaction,
              Amount: row.amount,
            })}
            downloadFilename={label}
            onSort={meta.sort.indexOf('id') > -1 ? false : setMeta}
            sort={meta.sort}
            subLabel={subLabel}
          />
        )}
        {chartType === 'line' && (
          <LineChart data={chart} isLoading={isLoading} />
        )}
        {chartType === 'bar' && (
          <BarChart data={chart} horizontal={false} isLoading={isLoading} />
        )}
        {chartType === 'horizontal-bar' && (
          <HorizontalBarChart data={chart} horizontal isLoading={isLoading} />
        )}
        {chartType === 'pie' && <PieChart data={chart} isLoading={isLoading} />}
      </div>
    </Card>
  );
}

HistogramCard.defaultProps = {
  filter: {},
  label: 'Chart',
  dateRange: true,
  switchMode: true,
  actionOptions: [],
  defaultMode: 'year',
  defaultChart: 'line',
  defaultSort: 'id:asc',
  tableFormat: [
    { key: 'name', label: 'Name' },
    { key: 'messages', label: 'Messages', width: 120 },
  ],
  renderSummary: null,
  customHeight: null,
  containerClass: '',
  cardClassName: '',
  extraParam: '',
};

HistogramCard.propTypes = {
  label: PropTypes.string,
  dateRange: PropTypes.bool,
  switchMode: PropTypes.bool,
  defaultMode: PropTypes.string,
  defaultSort: PropTypes.string,
  defaultChart: PropTypes.string,
  hook: PropTypes.func.isRequired,
  filter: PropTypes.instanceOf(Object),
  tableFormat: PropTypes.instanceOf(Array),
  actionOptions: PropTypes.instanceOf(Array),
  renderSummary: PropTypes.func,
  customHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  containerClass: PropTypes.string,
  cardClassName: PropTypes.string,
  extraParam: PropTypes.string,
};

export default HistogramCard;
